// Doradca maszyn <machine-advisor>: przegląd producentów i serii, dobór krokowy, karta maszyny, porównanie, zestaw.
// Przeniesione z modułu sklepu: machine-advisor.js — ocena i rozluźnianie warunków, ścieżki pytań, stan w URL,
// animacje szyn, zestaw z kodem i koszykiem (logika bez zmian). Dane, widoczność i kod SE-XXXX: @theme/advisor-data.
// Teksty z <script data-strings> (locale advisor.app); `{x}` w tekście to wstawiana wartość, `a|b|c` — odmiana.
import { Theme, cart, loadStyle, plural, reducedMotion, strings } from '@theme/core';
import {
  applyVisibility,
  assetUrl,
  decodeKitCode,
  encodeKitCode,
  kitOptions,
  linkShopProducts,
  loadAccessories,
  loadCatalog,
  loadShopProducts,
  readDraft,
  shownItem,
  slug,
} from '@theme/advisor-data';

const MAX_COMPARE = 4;
// Ta sama krzywa co --se-ease w advisor.css (Web Animations API nie czyta zmiennych CSS).
const SLIDE_EASE = 'cubic-bezier(0.28, 0.11, 0.32, 1)';
const SEARCH_ICON =
  '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M9 2a7 7 0 1 0 4.24 12.57l3.1 3.1 1.41-1.42-3.1-3.09A7 7 0 0 0 9 2Zm0 2a5 5 0 1 1 0 10A5 5 0 0 1 9 4Z"/></svg>';
const STEP_LABELS = ['cat', 'area', 'dry', 'noise', 'power', 'work', 'job', 'throughput', 'access', 'operator', 'where',
  'vtype', 'method', 'zone', 'purpose', 'results'];

const esc = (value) =>
  String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const fill = (text, values) => String(text || '').replace(/\{(\w+)\}/g, (match, key) => (key in values ? values[key] : match));
// Brakujące formy (tekst bez „|”) zastępuje poprzednią, żeby nie wypisać „undefined”.
const forms = (n, list) => {
  const [one, few = one, many = few] = String(list || '').split('|');
  return plural(n, one, few, many);
};
// Kontrakt danych (FR-18-36): z tej właściwości pozycji zamówienia sklep odczytuje kod zestawu — nie tłumaczyć.
const KIT_PROPERTY = 'Zestaw z konfiguratora';
const nf = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 2 });
const isEmpty = (value) => value == null || value === '' || value === '—';
// Miniatura z CDN zamiast oryginału (284–640 px). Kafle i karty mają `width/height: auto` z limitami, więc szerokość 1×
// jest rozmiarem obrazka w CSS: musi pokryć najszerszy kadr (zdjęcia 1,75:1 do 231 px), stąd THUMB = 240.
const THUMB = 240;
const thumb = (file, size) => {
  const x1 = esc(assetUrl(file, size));
  return `src="${x1}" srcset="${x1} 1x, ${esc(assetUrl(file, size * 2))} 2x"`;
};
const canAnimate = () => typeof Element.prototype.animate === 'function' && !reducedMotion();

// Sumy zestawu składamy z groszy wielu produktów — format jak dotąd: Intl pl-PL.
const money = (cents) =>
  new Intl.NumberFormat('pl-PL', { style: 'currency', currency: Theme.currency?.iso || 'PLN' }).format((cents || 0) / 100);

/** Wartość parametru do wyświetlenia („—” gdy producent nie podaje). */
function fmt(spec, value) {
  if (value == null || value === '') return '—';
  if (spec.type === 'num') return nf.format(value) + (spec.unit ? ` ${spec.unit}` : '');
  return String(value);
}

// Karta, którą przebudowa sekcji marki ma odnaleźć: id serii to „marka::Nazwa”, więc porównujemy wartości, nie selektor.
const findAnchor = (scope, key, value) =>
  value ? [...scope.querySelectorAll('[data-se-program], [data-se-series]')].find((node) => node.dataset[key] === value) : null;

// Przyciski, po których przebudowa innerHTML nie zmienia ekranu — fokus ma wrócić na ich odpowiednik.
const FOCUS_KEYS = ['seCat', 'seBrand', 'seTag', 'seDrop', 'seReset', 'seToggle', 'seClear'];
const canFocus = (node) => !node.disabled && node.getClientRects().length > 0;

/** Zapamiętuje przycisk z fokusem w jednym z obszarów jako parę atrybut–wartość (węzły zaraz zostaną podmienione). */
function focusMark(scopes) {
  const active = document.activeElement;
  const scope = active && scopes.find((node) => node !== active && node.contains(active));
  const key = scope && FOCUS_KEYS.find((name) => name in active.dataset);
  return key ? { scope, active, key, value: active.dataset[key] } : null;
}

/** Fokus na odpowiednik po przebudowie; gdy zniknął (np. ✕ usuniętej maszyny) — na pierwszy dostępny przycisk obszaru. */
function restoreFocus(mark) {
  if (!mark || (mark.active.isConnected && canFocus(mark.active))) return;
  const buttons = [...mark.scope.querySelectorAll('button')].filter(canFocus);
  const twin = buttons.find((node) => node.dataset[mark.key] === mark.value) || buttons[0];
  twin?.focus({ preventScroll: true });
}

class MachineAdvisor extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.start();
  }

  disconnectedCallback() {
    this.abort.abort();
    this.railObserver?.disconnect();
    this.revealObserver?.disconnect();
    this.railObserver = this.revealObserver = null;
    clearTimeout(this.revealFailsafe);
    clearTimeout(this.searchTimer);
  }

  async start() {
    const { signal } = this.abort;
    this.s = strings(this);
    this.config = JSON.parse(this.querySelector('script[data-advisor-config]').textContent);
    this.app = this.querySelector('[data-se-app]');
    this.stageNode = this.querySelector('[data-se-stage]');
    this.dock = this.querySelector('[data-se-dock]');
    this.traySlots = this.querySelector('[data-se-tray-slots]');
    this.compareDialog = this.querySelector('[data-se-compare]');
    this.detailDialog = this.querySelector('[data-se-detail-dialog]');
    this.kitDialog = this.querySelector('[data-se-kit-dialog]');
    const status = this.querySelector('[data-se-status]');
    // Kotwice formularzy, które Shopify dokleja po wysyłce; pierwszy writeUrl() je zdejmuje, więc odczyt tutaj.
    this.formHashes = [`#${this.config.askForm}`, `#se-kit-${this.config.id}`];
    const askPosted = location.hash === this.formHashes[0];

    try {
      // Arkusz aplikacji równolegle z danymi: aplikacja odsłania się dopiero z nim (loadStyle nie odrzuca).
      [this.data] = await Promise.all([this.loadData(), loadStyle(this.config.css)]);
    } catch {
      if (status) status.textContent = this.s.catalog_error;
      return;
    }
    if (signal.aborted) return;

    this.specByKey = new Map(this.data.specs.map((spec) => [spec.key, spec]));
    this.stage = 'start';
    this.answers = {};
    this.openProgram = '';
    this.openSeries = '';
    this.openFilter = '';
    this.searchOpen = false;
    this.openMachine = null;
    this.kitStage = 'pick';
    this.kit = { machineId: null, picked: new Set() };
    this.state = { q: '', cat: 'all', series: '', brands: new Set(), tags: new Set(), compare: [] };
    this.readUrl();

    status?.remove();
    this.app.hidden = false;
    this.dock.hidden = false;
    if (readDraft() && !this.querySelector('[data-se-draft]')) {
      this.app.insertAdjacentHTML('beforebegin', `<p class="se__warn" data-se-draft>${esc(this.s.draft_note)}</p>`);
    }

    this.bind();
    this.renderStage();
    if (this.deepLink) this.openDetail(this.deepLink);

    // Po wysłaniu wyceny Shopify przeładowuje stronę: pokazujemy potwierdzenie w oknie zestawu
    // (chyba że kotwica adresu wskazuje formularz pytania — oba formularze są typu contact).
    if (this.kitDialog.querySelector('[data-se-kit-posted]') && !askPosted) {
      // Shopify zgłasza sukces obu formularzom — potwierdzenie pytania byłoby drugim i walczyłoby o fokus.
      const askPosted = this.querySelector('[data-se-ask-posted]');
      if (askPosted) {
        askPosted.hidden = true;
        askPosted.removeAttribute('autofocus');
      }
      this.kitDialog.querySelector('[data-se-kit-formwrap]').hidden = false;
      this.kitDialog.showModal();
    }
  }

  async loadData() {
    const [catalog, products] = await Promise.all([loadCatalog(this.config.catalog), loadShopProducts(this.config.products)]);
    // Kopie maszyn: dopisujemy do nich produkty sklepu, a katalog jest współdzielony.
    const machines = catalog.machines.map((m) => ({ ...m, specs: { ...m.specs } }));
    this.shopByNumber = linkShopProducts(machines, products);
    const loaded = products.length > 0 && !products.partial;
    return applyVisibility({ ...catalog, machines }, loaded ? (m) => Boolean(m.shop) : null);
  }

  loadAcc() {
    return loadAccessories(this.config.accessories).catch(() => null);
  }

  machine(id) {
    return this.data.machines.find((m) => m.id === id);
  }

  brandName(id) {
    const brand = this.data.brands.find((b) => b.id === id);
    return brand ? brand.name : id;
  }

  imgUrl(machine) {
    return assetUrl(machine.img);
  }

  shopFor(item) {
    return item && item.no != null ? this.shopByNumber.get(String(item.no)) || null : null;
  }

  /* --------------------------------------------------------------- zdarzenia */

  bind() {
    const { signal } = this.abort;
    this.addEventListener('input', (event) => {
      const input = event.target.closest('[data-se-q]');
      if (!input) return;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.state.q = input.value.trim().toLowerCase();
        this.state.series = '';
        this.renderStage();
      }, 200);
    }, { signal });
    this.addEventListener('click', (event) => {
      if (this.kitDialog.contains(event.target)) this.onKitClick(event);
      else this.onClick(event);
    }, { signal });
    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.kitDialog.addEventListener('change', (event) => {
      const box = event.target.closest('[data-se-kit-item]');
      if (!box) return;
      if (box.checked) this.kit.picked.add(box.dataset.seKitItem);
      else this.kit.picked.delete(box.dataset.seKitItem);
      this.renderKitBar();
    }, { signal });
    // `close` czyści `d` w adresie; jawne sprzątanie jest też w closeDetail() i przy Escape.
    this.detailDialog.addEventListener('close', () => this.clearDetailState(), { signal });
    const form = this.kitDialog.querySelector('[data-se-kit-form]');
    form?.addEventListener('submit', () => {
      const field = form.querySelector('[data-se-kit-message]');
      if (field && !field.value.trim()) field.value = this.kitAsText();
    }, { signal });
  }

  onKeydown(event) {
    if (event.key === 'Enter' && event.target.closest('[data-se-head-code]')) {
      event.preventDefault();
      this.openKitFromCode();
    } else if (event.key === 'Enter' && event.target.closest('[data-se-kit-code-input]')) {
      event.preventDefault();
      this.restoreKit();
    } else if (event.key === 'Escape') {
      if (this.detailDialog.contains(event.target)) this.clearDetailState();
      if (!this.openFilter && !this.searchOpen) return;
      this.openFilter = '';
      this.searchOpen = false;
      this.syncBar();
    }
  }

  onClick(event) {
    const target = event.target;
    const on = (selector) => target.closest(selector);
    let node;

    if ((node = on('[data-se-pick]'))) {
      const previous = this.answers[this.stage];
      this.answers[this.stage] = node.dataset.sePick;
      // Inna kategoria to inna ścieżka — odpowiedzi z poprzedniej przepadają.
      if (this.stage === 'cat' && previous && previous !== this.answers.cat) {
        for (const key of Object.keys(this.answers)) if (key !== 'cat') delete this.answers[key];
      }
      this.goto(this.nextStep(this.stage));
    } else if ((node = on('[data-se-goto]'))) {
      this.goto(node.dataset.seGoto);
    } else if (on('[data-se-back]')) {
      const steps = this.flow().concat([{ id: 'results' }]);
      const at = steps.findIndex((step) => step.id === this.stage);
      this.goto(at > 0 ? steps[at - 1].id : 'start');
    } else if ((node = on('[data-se-rail]'))) {
      const track = node.closest('.se-rail').querySelector('.se-rail__track');
      const step = Math.max(240, Math.round(track.clientWidth * 0.8));
      const max = track.scrollWidth - track.clientWidth;
      this.glide(track, Math.min(max, Math.max(0, track.scrollLeft + (node.dataset.seRail === 'next' ? step : -step))));
    } else if ((node = on('[data-se-series]'))) {
      const id = node.dataset.seSeries;
      const opening = this.openSeries !== id;
      this.openSeries = opening ? id : '';
      this.leaveBrand(node.closest('[data-se-brand-id]'), opening, node, () =>
        this.renderBrand(id.split('::')[0], 'seSeries', id, opening ? 'open' : 'close')
      );
    } else if (on('[data-se-restart]')) {
      this.answers = {};
      this.goto('cat');
    } else if (on('[data-se-start]')) {
      this.state.series = '';
      this.goto('start');
    } else if ((node = on('[data-se-compare-picks]'))) {
      this.state.compare = node.dataset.seComparePicks.split(',').slice(0, MAX_COMPARE);
      this.renderTray();
      this.writeUrl();
      this.openCompare();
    } else if ((node = on('[data-se-cat]'))) {
      this.state.cat = node.dataset.seCat;
      this.state.series = '';
      this.renderStage();
    } else {
      this.onSecondaryClick(event);
    }
  }

  onSecondaryClick(event) {
    const target = event.target;
    const on = (selector) => target.closest(selector);
    let node;

    // Klik poza paskiem filtrów zamyka rozwiniętą listę.
    if (this.openFilter && !on('.se-filterbar')) {
      this.openFilter = '';
      this.syncBar();
    }

    if (on('[data-se-head-code-go]')) {
      this.openKitFromCode();
    } else if ((node = on('[data-se-drop]'))) {
      this.openFilter = this.openFilter === node.dataset.seDrop ? '' : node.dataset.seDrop;
      this.searchOpen = false;
      this.syncBar();
    } else if (on('[data-se-search]')) {
      this.searchOpen = !this.searchOpen;
      this.openFilter = '';
      this.syncBar();
      const input = this.searchOpen && this.stageNode.querySelector('[data-se-q]');
      if (input) {
        input.focus({ preventScroll: true });
        input.setSelectionRange(input.value.length, input.value.length);
      }
    } else if ((node = on('[data-se-program]'))) {
      const key = node.dataset.seProgram;
      const opening = this.openProgram !== key;
      this.openProgram = opening ? key : '';
      this.openSeries = '';
      this.leaveBrand(node.closest('[data-se-brand-id]'), opening, node, () =>
        this.renderBrand(key.split('::')[0], 'seProgram', key, opening ? 'open' : 'close')
      );
    } else if ((node = on('[data-se-brand]'))) {
      this.toggleIn(this.state.brands, node.dataset.seBrand);
    } else if ((node = on('[data-se-tag]'))) {
      this.toggleIn(this.state.tags, node.dataset.seTag);
    } else if (on('[data-se-reset]')) {
      Object.assign(this.state, { q: '', cat: 'all', series: '' });
      this.state.brands.clear();
      this.state.tags.clear();
      this.renderStage();
    } else if ((node = on('[data-se-detail]'))) {
      if (this.compareDialog.open) this.compareDialog.close();
      this.openDetail(node.dataset.seDetail);
    } else if ((node = on('[data-se-toggle]'))) {
      this.toggleCompare(node.dataset.seToggle);
    } else if (on('[data-se-clear]')) {
      this.state.compare = [];
      this.renderStage();
    } else if (on('[data-se-compare-open]')) {
      this.openCompare();
    } else if ((node = on('[data-se-kit-open]'))) {
      if (this.compareDialog.open) this.compareDialog.close();
      if (this.detailDialog.open) this.closeDetail();
      this.openKit(node.dataset.seKitOpen || null);
    } else if ((node = on('[data-se-close]')) || target.matches('dialog.se-dialog')) {
      // Przycisk ✕ albo klik w tło okna.
      const dialog = (node || target).closest('dialog');
      if (dialog === this.detailDialog) this.closeDetail();
      else dialog.close();
    }
  }

  toggleIn(set, id) {
    if (set.has(id)) set.delete(id);
    else set.add(id);
    this.state.series = '';
    this.renderStage();
  }

  clearDetailState() {
    if (!this.openMachine) return;
    this.openMachine = null;
    this.writeUrl();
  }

  closeDetail() {
    this.clearDetailState();
    if (this.detailDialog.open) this.detailDialog.close();
  }

  /* --------------------------------------------------------------- adres URL */

  readUrl() {
    const params = new URLSearchParams(location.search);
    const has = (list, value) => list.some((row) => row.id === value);

    if (has(this.data.categories, params.get('k'))) this.state.cat = params.get('k');
    for (const id of (params.get('m') || '').split(',').filter(Boolean)) {
      if (this.state.compare.length < MAX_COMPARE && this.machine(id)) this.state.compare.push(id);
    }
    if (this.machine(params.get('d'))) this.deepLink = params.get('d');

    if (has(this.data.categories, params.get('cat'))) this.answers.cat = params.get('cat');
    for (const step of (this.data.flow && this.data.flow[this.answers.cat]) || []) {
      const value = params.get(step.id);
      if (value && step.options.some((opt) => opt.id === value)) this.answers[step.id] = value;
    }

    const series = this.data.series.find((row) => row.id === params.get('seria'));
    if (series) {
      this.state.series = series.id;
      this.openSeries = series.id;
      this.openProgram = `${series.brand}::${series.program}`;
    }

    // Krok z adresu tylko, gdy da się do niego dojść (wcześniejsze pytania mają odpowiedź).
    const step = params.get('krok');
    if (step) {
      const steps = this.flow().map((x) => x.id);
      const at = steps.indexOf(step);
      const reachable = step === 'results'
        ? steps.every((id) => this.answers[id])
        : at >= 0 && steps.slice(0, at).every((id) => this.answers[id]);
      if (reachable) this.stage = step;
    }
  }

  // replaceState: stan do udostępnienia linkiem, bez wpisów w historii. Usuwamy tylko klucze obecne w odpowiedziach.
  writeUrl() {
    const params = new URLSearchParams(location.search);
    const set = (key, value) => (value ? params.set(key, value) : params.delete(key));
    set('krok', this.stage !== 'start' && this.stage);
    set('seria', this.stage === 'start' ? this.openSeries : this.state.series);
    for (const key of Object.keys(this.answers)) set(key, this.answers[key]);
    set('k', this.state.cat !== 'all' && this.state.cat);
    set('d', this.openMachine);
    set('m', this.state.compare.join(','));
    // Ślad wysłanego formularza w linku do udostępnienia pokazałby odbiorcy cudze potwierdzenie wysyłki.
    params.delete('contact_posted');
    const hash = this.formHashes.includes(location.hash) ? '' : location.hash;
    const qs = params.toString();
    history.replaceState(history.state, '', location.pathname + (qs ? `?${qs}` : '') + hash);
  }

  /* ------------------------------------------------------------------ ekrany */

  goto(stage) {
    this.stage = stage;
    this.renderStage();
    // Po zmianie kroku wracamy na górę sekcji — 16 px pod przyklejonym paskiem nagłówka (0, gdy nagłówek nie klei się).
    const bar = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height-stuck')) || 0;
    const top = this.parentElement.getBoundingClientRect().top + scrollY - bar - 16;
    if (scrollY > top) scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  renderStage() {
    const active = document.activeElement;
    const caret = this.stageNode.contains(active) && active.matches('[data-se-q]') ? active.selectionStart : null;
    const focus = focusMark([this.stageNode, this.dock]);

    if (this.stage === 'start') this.stageNode.innerHTML = this.stageStart();
    else if (this.stage === 'results') this.stageNode.innerHTML = this.stageResults();
    else this.stageNode.innerHTML = this.stageQuestion();

    if (this.stage === 'start') {
      this.syncFilters();
      this.syncRails();
      // Przebudowa w trakcie pisania nie może zabrać fokusu z pola szukania.
      const input = caret != null && this.stageNode.querySelector('[data-se-q]');
      if (input) {
        input.focus({ preventScroll: true });
        input.setSelectionRange(caret, caret);
      }
    }
    this.reveal();
    this.renderTray();
    restoreFocus(focus);
    this.writeUrl();
  }

  /*
   * Sekcje marek pod krawędzią ekranu wjeżdżają przy przewijaniu. Ukrywa skrypt (bez IntersectionObserver nic),
   * a bezpiecznik po 1,5 s odsłania resztę, gdy obserwator milczy (wstrzymana pętla klatek).
   * Animujemy ekran, nie kontener aplikacji.
   */
  reveal() {
    if ('IntersectionObserver' in window) {
      this.revealObserver ??= new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          this.revealObserver.unobserve(entry.target);
        }
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.01 });

      const fold = innerHeight || 800;
      const pending = [];
      for (const node of this.app.querySelectorAll('.se-reveal:not(.is-reveal)')) {
        node.classList.add('is-reveal');
        if (node.getBoundingClientRect().top < fold * 0.9) {
          node.classList.add('is-in');
        } else {
          pending.push(node);
          this.revealObserver.observe(node);
        }
      }
      clearTimeout(this.revealFailsafe);
      if (pending.length) this.revealFailsafe = setTimeout(() => pending.forEach((node) => node.classList.add('is-in')), 1500);
    }

    this.stageNode.classList.remove('is-entering');
    void this.stageNode.offsetWidth;
    this.stageNode.classList.add('is-entering');
  }

  /* ------------------------------------------------- przegląd: marki i serie */

  seriesMatches(row) {
    const { q, cat, brands, tags } = this.state;
    if (brands.size && !brands.has(row.brand)) return false;
    if (!q && cat === 'all' && !tags.size) return true;
    return this.data.machines.some((m) => {
      if (`${m.brand}::${m.series}` !== row.id) return false;
      if (cat !== 'all' && !m.cats.includes(cat)) return false;
      for (const tag of tags) if (!m.tags.includes(tag)) return false;
      if (q) {
        const hay = `${m.name} ${m.series} ${m.family} ${m.program} ${m.tagline} ${this.brandName(m.brand)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }

  get filtersActive() {
    const { q, cat, brands, tags } = this.state;
    return q !== '' || cat !== 'all' || brands.size > 0 || tags.size > 0;
  }

  stageStart() {
    const s = this.s;
    const filtered = this.data.series.filter((row) => this.seriesMatches(row));
    const machines = filtered.reduce((sum, row) => sum + row.count, 0);
    const body = this.data.brands
      .filter((brand) => !this.state.brands.size || this.state.brands.has(brand.id))
      .map((brand) => {
        const programs = this.programsOf(filtered, brand.id);
        return programs.length ? this.brandSection(brand, programs) : '';
      })
      .join('');
    const total = this.filtersActive ? ` ${fill(s.count_of, { n: this.data.machines.length })}` : '';
    const note = this.data.meta && this.data.meta.note;

    return `
      ${this.filterBar()}
      <p class="se-count">${filtered.length} ${esc(forms(filtered.length, s.forms_series))} · ${machines} ${esc(forms(machines, s.forms_machines))}${esc(total)}</p>
      ${body || `<p class="se-empty">${esc(s.no_series)} <button type="button" class="se-link" data-se-reset>${esc(s.clear_filters)}</button></p>`}
      ${note ? `<p class="se-note">${esc(note)}</p>` : ''}`;
  }

  // Pasek w jednym rzędzie: każde kryterium to zwinięty przycisk z listą; stan otwarcia przeżywa przerysowanie.
  filterBar() {
    const s = this.s;
    const drop = (id, label, chips, active) => {
      const open = this.openFilter === id;
      return `
        <div class="se-drop${open ? ' is-open' : ''}">
          <button type="button" class="se-drop__btn${active ? ' is-active' : ''}" data-se-drop="${id}" aria-expanded="${open}">
            ${esc(label)}${active ? `<span class="se-drop__count">${active}</span>` : ''}
            <span class="se-drop__chevron" aria-hidden="true"></span>
          </button>
          <div class="se-drop__panel" role="group" aria-label="${esc(label)}"${open ? '' : ' hidden'}>${chips}</div>
        </div>`;
    };
    const chip = (attr, id, name, extra = '') =>
      `<button type="button" class="se-chip${attr === 'tag' ? ' se-chip--tag' : ''}" data-se-${attr}="${esc(id)}"${extra}>${esc(name)}</button>`;
    const catChips = [{ id: 'all', name: s.cat_all }, ...this.data.categories].map((c) => chip('cat', c.id, c.name)).join('');
    const brandChips = this.data.brands.map((b) => chip('brand', b.id, b.name, ` title="${esc(b.short)}"`)).join('');
    const tagChips = this.data.tags.map((t) => chip('tag', t.id, t.name)).join('');

    return `
      <div class="se-filterbar">
        <div class="se-filterbar__row">
          <div class="se-search${this.searchOpen ? ' is-open' : ''}">
            <button type="button" class="se-search__btn" data-se-search aria-expanded="${this.searchOpen}">
              ${SEARCH_ICON}<span class="se-search__label">${esc(this.state.q || s.search)}</span>
            </button>
            <div class="se-search__field"${this.searchOpen ? '' : ' hidden'}>
              ${SEARCH_ICON}
              <input type="search" data-se-q value="${esc(this.state.q)}" placeholder="${esc(s.search_placeholder)}" aria-label="${esc(s.search)}" autocomplete="off">
              <button type="button" class="se-search__close" data-se-search aria-label="${esc(s.search_close)}">✕</button>
            </div>
          </div>
          ${drop('brand', s.filter_brand, brandChips, this.state.brands.size)}
          ${drop('cat', s.filter_cat, catChips, this.state.cat === 'all' ? 0 : 1)}
          ${drop('tag', s.filter_tag, tagChips, this.state.tags.size)}
          <button type="button" class="se-chip se-chip--clear" data-se-reset hidden>${esc(s.clear_filters)}</button>
        </div>
      </div>`;
  }

  // Otwarcie listy i pola szukania przestawia klasy zamiast przebudowy (kursor w polu zostaje).
  syncBar() {
    for (const button of this.stageNode.querySelectorAll('[data-se-drop]')) {
      const open = this.openFilter === button.dataset.seDrop;
      const box = button.closest('.se-drop');
      box.classList.toggle('is-open', open);
      box.querySelector('.se-drop__panel').hidden = !open;
      button.setAttribute('aria-expanded', String(open));
    }
    const search = this.stageNode.querySelector('.se-search');
    if (search) {
      search.classList.toggle('is-open', this.searchOpen);
      search.querySelector('.se-search__field').hidden = !this.searchOpen;
      search.querySelector('.se-search__btn').setAttribute('aria-expanded', String(this.searchOpen));
    }
  }

  syncFilters() {
    const mark = (selector, isOn) => {
      for (const button of this.stageNode.querySelectorAll(selector)) {
        const on = isOn(button);
        button.classList.toggle('is-active', on);
        button.setAttribute('aria-pressed', String(on));
      }
    };
    mark('[data-se-cat]', (b) => b.dataset.seCat === this.state.cat);
    mark('[data-se-brand]', (b) => this.state.brands.has(b.dataset.seBrand));
    mark('[data-se-tag]', (b) => this.state.tags.has(b.dataset.seTag));
    const dirty = this.state.series !== '' || this.filtersActive;
    for (const button of this.stageNode.querySelectorAll('.se-chip--clear')) button.hidden = !dirty;
    this.syncBar();
  }

  /** Programy marki (grupy serii o tym samym `program`). */
  programsOf(rows, brandId) {
    const out = [];
    for (const row of rows) {
      if (row.brand !== brandId) continue;
      const found = out.find((p) => p.name === row.program);
      if (found) found.rows.push(row);
      else out.push({ name: row.program, note: row.programNote || '', rows: [row] });
    }
    return out;
  }

  /*
   * Drabina w głąb: wybrany program (i seria) zostaje po lewej, następny poziom wysuwa się obok.
   *   [program] → serie na szynie;   [program] [seria] → modele na szynie.
   */
  brandSection(brand, programs) {
    const s = this.s;
    const series = programs.reduce((sum, p) => sum + p.rows.length, 0);
    const count = programs.reduce((sum, p) => sum + p.rows.reduce((n, row) => n + row.count, 0), 0);
    const [openBrand, openName] = (this.openProgram || '').split('::');
    const open = openBrand === brand.id ? programs.find((p) => p.name === openName) : null;
    const meta = [
      brand.country,
      `${programs.length} ${forms(programs.length, s.forms_programs)}`,
      `${series} ${forms(series, s.forms_series)}`,
      `${count} ${forms(count, s.forms_machines)}`,
    ].join(' · ');
    const head = `
      <header class="se-brand__head">
        <h2 class="se-brand__name">${esc(brand.name)}</h2>
        <p class="se-brand__meta">${esc(meta)}</p>
        <p class="se-brand__short">${esc(brand.short)}</p>
      </header>`;

    if (!open) {
      return `
        <section class="se-brand se-reveal" data-se-brand-id="${esc(brand.id)}">
          ${head}
          ${this.rail(programs.map((p) => this.programCard(brand.id, p, false)).join(''), 'se-progs')}
        </section>`;
    }

    const chosen = open.rows.find((row) => row.id === this.openSeries);
    return `
      <section class="se-brand se-reveal" data-se-brand-id="${esc(brand.id)}">
        ${head}
        <div class="se-brand__split${chosen ? ' se-brand__split--series' : ''}">
          <div class="se-progs se-progs--chosen">${this.programCard(brand.id, open, true)}</div>
          ${chosen
            ? `<div class="se-chosen">${this.seriesCard(chosen)}</div>${this.modelsRail(chosen)}`
            : this.rail(open.rows.map((row) => this.seriesCard(row)).join(''))}
        </div>
      </section>`;
  }

  rail(cards, trackClass = '') {
    return `
      <div class="se-rail">
        <div class="se-rail__track${trackClass ? ` ${trackClass}` : ''}">${cards}</div>
        <button type="button" class="se-rail__btn se-rail__btn--prev" data-se-rail="prev" aria-label="${esc(this.s.rail_prev)}">‹</button>
        <button type="button" class="se-rail__btn se-rail__btn--next" data-se-rail="next" aria-label="${esc(this.s.rail_next)}">›</button>
      </div>`;
  }

  programCard(brandId, program, active) {
    const s = this.s;
    const models = program.rows.reduce((sum, row) => sum + row.count, 0);
    const flagship = program.rows.slice().sort((a, b) => b.count - a.count)[0];
    return `
      <button type="button" class="se-prog${active ? ' is-active' : ''}" data-se-program="${esc(`${brandId}::${program.name}`)}" aria-expanded="${Boolean(active)}">
        <span class="se-prog__media">
          <img ${thumb(flagship.img, THUMB)} alt="" loading="lazy" width="200" height="150">
          <span class="se-prog__toggle" aria-hidden="true"><span class="se-prog__chevron"></span></span>
        </span>
        <span class="se-prog__body">
          <span class="se-prog__name">${esc(program.name)}</span>
          ${program.note ? `<span class="se-prog__note">${esc(program.note)}</span>` : ''}
          <span class="se-prog__count">${program.rows.length} ${esc(forms(program.rows.length, s.forms_series))} · ${models} ${esc(forms(models, s.forms_models))}</span>
          <span class="se-prog__hint">${esc(active ? s.series_hide : s.series_show)}</span>
        </span>
      </button>`;
  }

  seriesCard(row) {
    const open = this.openSeries === row.id;
    const stats = row.stats.length ? row.stats.join(' · ') : '';
    return `
      <button type="button" class="se-series__card${open ? ' is-open' : ''}" data-se-series="${esc(row.id)}" title="${esc(row.note)}" aria-expanded="${open}">
        <span class="se-series__media"><img ${thumb(row.img, THUMB)} alt="" loading="lazy" width="200" height="150"></span>
        <span class="se-series__body">
          <span class="se-series__name">${esc(row.name)}</span>
          <span class="se-series__family">${esc(row.family)}</span>
          ${stats ? `<span class="se-series__stats">${esc(stats)}</span>` : ''}
          <span class="se-series__count">${row.count} ${esc(forms(row.count, this.s.forms_models))} ${open ? '↑' : '↓'}</span>
        </span>
      </button>`;
  }

  modelsRail(row) {
    const machines = this.data.machines.filter((m) => `${m.brand}::${m.series}` === row.id);
    if (!machines.length) return '';
    return `<div class="se-models" data-se-models>${this.rail(machines.map((m) => this.modelCard(m)).join(''), 'se-models__track')}</div>`;
  }

  // Karta modelu ma wysokość kafla programu i serii — zejście w głąb nie zmienia wysokości strony.
  modelCard(m) {
    const s = this.s;
    const spec = [m.specs.perfText, m.specs.tankSolution ? fill(s.tank, { n: m.specs.tankSolution }) : ''].filter(Boolean).join(' · ');
    const picked = this.state.compare.includes(m.id);
    const full = this.state.compare.length >= MAX_COMPARE;
    const price = m.shop && m.shop.price;
    return `
      <div class="se-model${picked ? ' is-picked' : ''}${price ? ' se-model--priced' : ''}">
        <button type="button" class="se-model__open" data-se-detail="${esc(m.id)}">
          <span class="se-model__media"><img ${thumb(m.img, THUMB)} alt="" loading="lazy" width="200" height="150"></span>
          <span class="se-model__body">
            <span class="se-model__name">${esc(m.name)}</span>
            <span class="se-model__family">${esc(m.family)}</span>
            ${spec ? `<span class="se-model__stats">${esc(spec)}</span>` : ''}
            ${price ? `<span class="se-model__price">${esc(price)}</span>` : ''}
          </span>
        </button>
        <button type="button" class="se-model__pick${picked ? ' is-picked' : ''}" data-se-toggle="${esc(m.id)}" aria-pressed="${picked}"${!picked && full ? ' disabled' : ''}>${esc(picked ? s.compare_on : s.compare)}</button>
      </div>`;
  }

  /*
   * Przewijanie szyny strzałką: snap wyłączony na czas ruchu, a gdy `smooth` zostanie po cichu pominięte
   * (tor po 250 ms nie drgnął), skok bez animacji.
   */
  glide(track, target) {
    const from = track.scrollLeft;
    if (from === target) return;
    clearTimeout(track.seSnap);
    track.style.scrollSnapType = 'none';
    track.scrollTo({ left: target, behavior: reducedMotion() ? 'auto' : 'smooth' });
    setTimeout(() => {
      if (track.scrollLeft === from) track.scrollTo({ left: target, behavior: 'auto' });
      track.seSync?.();
    }, 250);
    track.seSnap = setTimeout(() => {
      track.style.scrollSnapType = '';
      track.seSync?.();
    }, 700);
  }

  // Strzałki tylko przy przepełnieniu toru, nieaktywne na końcach (tolerancja 2 px).
  syncRails(scope = this.stageNode) {
    for (const rail of scope.querySelectorAll('.se-rail')) {
      const track = rail.querySelector('.se-rail__track');
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        rail.classList.toggle('is-scrollable', max > 0);
        rail.querySelector('[data-se-rail="prev"]').disabled = track.scrollLeft <= 2;
        rail.querySelector('[data-se-rail="next"]').disabled = track.scrollLeft >= max;
      };
      if (!track.seSync) track.addEventListener('scroll', update, { passive: true, signal: this.abort.signal });
      track.seSync = update;
      update();
    }
    if (!this.railObserver && 'ResizeObserver' in window) {
      this.railObserver = new ResizeObserver(() => this.stage === 'start' && this.syncRails());
      this.railObserver.observe(this);
    }
  }

  slideOut(node, distance) {
    node.classList.add('is-sliding');
    return node.animate([{ transform: 'none' }, { transform: `translateX(${Math.round(distance)}px)` }], {
      duration: 380,
      easing: SLIDE_EASE,
      fill: 'forwards',
    });
  }

  slideIn(node, from) {
    return node.animate([{ transform: `translateX(${Math.round(from)}px)` }, { transform: 'none' }], { duration: 420, easing: SLIDE_EASE });
  }

  /*
   * Zejście w głąb: cały rząd jedzie w lewo o odległość klikniętej karty od krawędzi toru (tor obcina karty).
   * Powrót: karty niższych poziomów wyjeżdżają za lewą krawędź, każda o własny dystans.
   */
  leaveBrand(section, opening, keep, done) {
    if (!section || !canAnimate()) return done();

    if (opening) {
      const track = keep.closest('.se-rail__track');
      if (!track) return done();
      const shift = keep.getBoundingClientRect().left - track.getBoundingClientRect().left;
      if (Math.abs(shift) < 2) return done();
      for (const card of track.children) this.slideOut(card, -shift);
      setTimeout(done, 390);
      return;
    }

    let leaving = 0;
    for (const strip of section.querySelectorAll('.se-rail__track')) {
      if (strip.contains(keep)) continue;
      const edge = strip.getBoundingClientRect().left;
      for (const card of strip.children) {
        this.slideOut(card, -(card.getBoundingClientRect().right - edge + 24));
        leaving += 1;
      }
    }
    if (!leaving) return done();
    setTimeout(done, 380);
  }

  // Przerysowanie jednej marki (nie całego ekranu) — przewinięcia innych szyn i ich stan zostają.
  renderBrand(brandId, anchorKey, anchorValue, mode) {
    const node = this.stageNode.querySelector(`[data-se-brand-id="${CSS.escape(brandId)}"]`);
    const brand = this.data.brands.find((b) => b.id === brandId);
    const programs = this.programsOf(this.data.series.filter((row) => this.seriesMatches(row)), brandId);
    if (!node || !brand || !programs.length) return this.renderStage();

    const anchor = findAnchor(node, anchorKey, anchorValue);
    const before = anchor ? anchor.getBoundingClientRect() : null;
    const hadFocus = node.contains(document.activeElement);
    const oldTrack = anchor ? anchor.closest('.se-rail__track') : null;
    const oldScroll = oldTrack ? oldTrack.scrollLeft : 0;

    const holder = document.createElement('div');
    holder.innerHTML = this.brandSection(brand, programs);
    const fresh = holder.firstElementChild;
    fresh.classList.add('is-reveal', 'is-in');
    node.replaceWith(fresh);

    this.syncRails(fresh);
    this.settle(fresh, anchorKey, anchorValue, before, oldScroll, mode);
    if (hadFocus) findAnchor(fresh, anchorKey, anchorValue)?.focus({ preventScroll: true });
    this.renderTray();
    this.writeUrl();
  }

  /*
   * Dojazd do nowego układu bez skoku: na szynie kliknięta karta dojeżdża do lewej krawędzi toru (przy zwijaniu
   * rząd wraca z przesunięcia), poza szyną kafel programu startuje z dawnego miejsca (FLIP).
   */
  settle(section, anchorKey, anchorValue, before, oldScroll, mode) {
    if (!before) return;
    const target = findAnchor(section, anchorKey, anchorValue);
    if (!target) return;

    const track = target.closest('.se-rail__track');
    if (track) {
      track.scrollLeft = oldScroll;
      const left = target.getBoundingClientRect().left - track.getBoundingClientRect().left;
      if (mode === 'close') {
        if (canAnimate()) for (const card of track.children) this.slideIn(card, -left);
        return;
      }
      this.glide(track, track.scrollLeft + left);
      return;
    }

    const dx = before.left - target.getBoundingClientRect().left;
    if (Math.abs(dx) < 1) return;
    target.classList.add('is-flying');
    target.style.transform = `translateX(${dx}px)`;
    void target.offsetWidth;
    target.style.transform = '';
    setTimeout(() => target.classList.remove('is-flying'), 500);
  }

  /* ------------------------------------------------------- dobór krokowy */

  // Pierwszy krok zawsze ten sam; dalsza ścieżka zależy od kategorii (`flow` katalogu).
  flow() {
    const rest = (this.data.flow && this.data.flow[this.answers.cat]) || [];
    return [{ id: 'cat', label: this.s.question_cat, lead: this.s.lead_cat, options: null }].concat(rest);
  }

  nextStep(afterId) {
    const steps = this.flow();
    const from = steps.findIndex((step) => step.id === afterId);
    for (let i = from + 1; i < steps.length; i += 1) if (!this.answers[steps[i].id]) return steps[i].id;
    return 'results';
  }

  stepLabel(step) {
    return STEP_LABELS.includes(step.id) ? this.s[`step_${step.id}`] : step.label;
  }

  progress(activeId) {
    const steps = this.flow().concat([{ id: 'results' }]);
    const activeIndex = steps.findIndex((step) => step.id === activeId);
    const items = steps.map((step, index) => {
      const done = index < activeIndex;
      const inner = `<span class="se-steps__num">${done ? '✓' : String(index + 1).padStart(2, '0')}</span><span class="se-steps__label">${esc(this.stepLabel(step))}</span>`;
      const body = done
        ? `<button type="button" class="se-steps__btn" data-se-goto="${esc(step.id)}">${inner}</button>`
        : `<span class="se-steps__btn">${inner}</span>`;
      const now = index === activeIndex;
      return `<li class="se-steps__item${now ? ' is-now' : ''}${done ? ' is-done' : ''}"${now ? ' aria-current="step"' : ''}>${body}</li>`;
    });
    return `<ol class="se-steps">${items.join('')}</ol>`;
  }

  // Panel kroku: pytanie i odpowiedzi na jednej granatowej płaszczyźnie, pole kodu jako skrót przez dobór.
  stageHead(kicker, question, lead, activeId, body) {
    const s = this.s;
    const codeId = `se-head-code-${this.config.id}`;
    return `
      <section class="se-head">
        <div class="se-head__main">
          ${this.progress(activeId)}
          <p class="se-head__kicker">${esc(kicker)}</p>
          <h2 class="se-head__question">${esc(question)}</h2>
          ${lead ? `<p class="se-head__lead">${esc(lead)}</p>` : ''}
          <div class="se-head__actions">
            <button type="button" class="se-head__back" data-se-back>${esc(s.back)}</button>
            <button type="button" class="se-head__browse" data-se-start>${esc(s.browse)}</button>
          </div>
        </div>
        <div class="se-head__code">
          <label class="se-head__code-label" for="${codeId}">${esc(s.code_label)}</label>
          <div class="se-head__code-row">
            <input type="text" id="${codeId}" data-se-head-code placeholder="${esc(s.code_placeholder)}" autocomplete="off" spellcheck="false">
            <button type="button" class="se-head__code-go" data-se-head-code-go>${esc(s.code_load)}</button>
          </div>
          <p class="se-head__code-note" data-se-head-code-note role="status"></p>
        </div>
        ${body ? `<div class="se-head__body">${body}</div>` : ''}
      </section>`;
  }

  /** Plik ilustracji kategorii: największa maszyna, najlepiej ze zdjęciem na wyciętym tle (`cut`). */
  catImage(catId) {
    const all = this.data.machines.filter((m) => m.cats.includes(catId) && m.img);
    if (!all.length) return '';
    const cut = all.filter((m) => m.cut !== false);
    const size = (m) => m.specs.perf || m.specs.tankSolution || 0;
    return (cut.length ? cut : all).slice().sort((a, b) => size(b) - size(a))[0].img;
  }

  stageQuestion() {
    const s = this.s;
    const steps = this.flow();
    const index = steps.findIndex((step) => step.id === this.stage);
    const step = index < 0 ? steps[0] : steps[index];
    const rows = step.options
      ? step.options.map((opt) => ({ id: opt.id, label: opt.name, hint: opt.hint }))
      : this.data.categories.map((c) => {
          const n = this.data.machines.filter((m) => m.cats.includes(c.id)).length;
          return { id: c.id, label: c.name, hint: c.short, img: this.catImage(c.id), meta: fill(s.in_catalog, { n, machines: forms(n, s.forms_machines) }) };
        });
    const picked = this.answers[step.id];
    const tiles = rows
      .map((row) => `
        <button type="button" class="se-tile${picked === row.id ? ' is-picked' : ''}" data-se-pick="${esc(row.id)}" aria-pressed="${picked === row.id}">
          ${row.img ? `<span class="se-tile__media"><img ${thumb(row.img, THUMB)} alt="" loading="lazy" width="220" height="160"></span>` : ''}
          <span class="se-tile__body">
            <span class="se-tile__label">${esc(row.label)}</span>
            ${row.hint ? `<span class="se-tile__hint">${esc(row.hint)}</span>` : ''}
            ${row.meta ? `<span class="se-tile__meta">${esc(row.meta)}</span>` : ''}
          </span>
        </button>`)
      .join('');
    // Bez kategorii nie znamy długości ścieżki — wtedy bez sumy kroków.
    const kicker = this.answers.cat ? fill(s.kicker_step, { i: Math.max(index, 0) + 1, n: steps.length + 1 }) : s.kicker_first;
    return this.stageHead(kicker, step.label, step.lead, this.stage, `<div class="se-tiles">${tiles}</div>`);
  }

  stageResults() {
    const s = this.s;
    const matches = this.wizardMatches();
    const cat = this.data.categories.find((c) => c.id === this.answers.cat);
    const given = this.flow()
      .filter((step) => step.options && this.answers[step.id])
      .map((step) => ({ step, opt: step.options.find((o) => o.id === this.answers[step.id]) }))
      .filter((x) => x.opt);
    const summary = [cat && cat.name].concat(given.map((x) => x.opt.name.toLowerCase())).filter(Boolean).join(' · ');
    // „Krok N z N” z długości ścieżki (BG-20).
    const last = this.flow().length + 1;
    const kicker = fill(s.kicker_step, { i: last, n: last });

    if (!matches.length) {
      return this.stageHead(kicker, s.results_none_title, summary, 'results', `<p class="se-empty">${esc(s.results_none)}</p>`);
    }

    const chips = [{ step: { id: 'cat' }, opt: { name: cat ? cat.name : '' } }]
      .concat(given)
      .map((x) => `<button type="button" class="se-recap__chip" data-se-goto="${esc(x.step.id)}">${esc(x.opt.name)}<span aria-hidden="true">✎</span></button>`)
      .join('');
    const relaxed = this.relaxed.length
      ? `<p class="se-relaxed">${esc(fill(s.relaxed, { list: this.relaxed.map((r) => r.opt.name.toLowerCase()).join(', ') }))}</p>`
      : '';
    const top = matches[0];
    const rest = matches.slice(1, 4);
    const price = top.shop && top.shop.price;
    const alts = rest.length
      ? `
        <section class="se-alts">
          <h3 class="se-alts__title">${esc(s.alts)}</h3>
          <div class="se-alts__grid">${rest.map((m) => this.altCard(m)).join('')}</div>
          <div class="se-alts__actions">
            <button type="button" class="se-btn se-btn--ghost" data-se-compare-picks="${esc(matches.slice(0, 4).map((m) => m.id).join(','))}">${esc(fill(s.compare_these, { n: rest.length + 1, machines: forms(rest.length + 1, s.forms_machines_acc) }))}</button>
            <button type="button" class="se-link" data-se-restart>${esc(s.restart)}</button>
          </div>
        </section>`
      : '';

    const body = `
      <div class="se-recap"><span class="se-recap__label">${esc(s.answers)}</span>${chips}</div>
      ${relaxed}
      <article class="se-hero">
        <div class="se-hero__media"><img src="${esc(this.imgUrl(top))}" alt="${esc(top.name)}" width="420" height="420"></div>
        <div class="se-hero__body">
          <p class="se-hero__badge badge">${esc(s.best_fit)}${top.fit ? ` · ${top.fit}%` : ''}</p>
          <p class="se-hero__brand">${esc(this.brandName(top.brand))}</p>
          <h3 class="se-hero__name">${esc(top.name)}</h3>
          <p class="se-hero__family">${esc(top.family)}</p>
          <p class="se-hero__tagline">${esc(top.tagline)}</p>
          ${price ? `<p class="se-hero__price">${esc(price)}</p>` : `<p class="se-hero__price se-hero__price--ask">${esc(s.price_ask)}</p>`}
          ${top.why.length ? `<p class="se-hero__why"><strong>${esc(s.why)}</strong> ${esc(top.why.join(', '))}</p>` : ''}
          <ul class="se-hero__hi">${top.hi.map((h) => `<li>${esc(h)}</li>`).join('')}</ul>
          <div class="se-hero__actions">
            <button type="button" class="se-btn" data-se-kit-open="${esc(top.id)}">${esc(s.kit_start)}</button>
            <button type="button" class="se-btn se-btn--ghost" data-se-detail="${esc(top.id)}">${esc(s.specs)}</button>
          </div>
        </div>
      </article>
      ${alts}`;
    return this.stageHead(kicker, s.results_title, summary, 'results', body);
  }

  altCard(m) {
    const s = this.s;
    const worse = m.against.length ? ` · ${fill(s.fit_worse, { list: m.against.join(', ') })}` : '';
    return `
      <div class="se-alt">
        ${this.card(m)}
        <p class="se-alt__fit">
          <span class="se-alt__bar"><span style="width: ${m.fit || 0}%"></span></span>
          ${esc(fill(s.fit, { n: m.fit || 0 }) + worse)}
        </p>
      </div>`;
  }

  /*
   * Ocena dopasowania (FR-18-19): reguły z katalogu (`require`, `want`, `avoid`, `size`, `max_kg`). Twardy warunek,
   * który wyzerowałby pulę, jest zdejmowany i zapamiętywany — klient dowiaduje się o tym przy wyniku.
   */
  wizardMatches() {
    const s = this.s;
    const chosen = [];
    for (const step of this.flow()) {
      const opt = step.options && step.options.find((o) => o.id === this.answers[step.id]);
      if (opt) chosen.push({ step, opt });
    }

    const relaxed = [];
    let pool = this.data.machines.filter((m) => m.cats.includes(this.answers.cat));
    for (const { step, opt } of chosen) {
      if (!opt.require) continue;
      const next = pool.filter((m) => opt.require.every((tag) => m.tags.includes(tag)));
      if (next.length) pool = next;
      else relaxed.push({ step, opt });
    }

    const scored = pool.map((m) => {
      const sp = m.specs;
      // Wspólna miara wielkości maszyny; brak danych to nota neutralna, nie najlepsza.
      const size = (sp.perf || 0) / 100 + (sp.tankSolution || sp.dustBag || sp.hopper || 0) / 10 + (sp.width || 0) / 100 +
        (sp.steamFlow || 0) / 40 + (sp.steamPressure || 0) / 2;
      const known = sp.perf || sp.tankSolution || sp.dustBag || sp.hopper || sp.width || sp.weight;
      let score = 50;
      const why = [];
      const against = [];

      for (const { opt } of chosen) {
        const relaxedHere = relaxed.some((r) => r.opt === opt);
        for (const tag of opt.want || []) {
          if (m.tags.includes(tag)) {
            score += 18;
            if (opt.why) why.push(opt.why);
          } else {
            score -= 4;
          }
        }
        for (const tag of opt.avoid || []) {
          if (!m.tags.includes(tag)) continue;
          score -= 22;
          against.push(this.tagName(tag));
        }
        if (opt.require && !relaxedHere) {
          score += 20;
          if (opt.why) why.push(opt.why);
        }
        if (opt.size && known) {
          if (opt.size === 'small') score += 26 - size * 2;
          else if (opt.size === 'mid') score += 26 - Math.abs(size - 8) * 3;
          else {
            score += Math.min(30, size * 2);
            if (sp.perf) why.push(fill(s.why_perf, { n: nf.format(sp.perf) }));
          }
        }
        if (opt.max_kg && sp.weight) {
          const mass = fill(s.why_mass, { n: nf.format(sp.weight) });
          if (sp.weight <= opt.max_kg) {
            score += 16;
            why.push(mass);
          } else {
            score -= 14;
            against.push(mass);
          }
        }
      }
      return { machine: m, score, why: [...new Set(why.filter(Boolean))].slice(0, 3), against: [...new Set(against)].slice(0, 2) };
    });

    scored.sort((a, b) => b.score - a.score);
    // Procent względem czołowej maszyny, nie teoretycznego maksimum.
    const best = scored.length ? Math.max(scored[0].score, 1) : 1;
    this.relaxed = relaxed;
    return scored.map((x) => ({
      ...x.machine,
      why: x.why,
      against: x.against,
      fit: Math.max(35, Math.min(99, Math.round((x.score / best) * 100))),
    }));
  }

  tagName(id) {
    const tag = this.data.tags.find((t) => t.id === id);
    return tag ? tag.name.toLowerCase() : id;
  }

  /** Maszyny zgodne z filtrami przeglądu (lista wyboru w zestawie). */
  visible() {
    const { q, cat, series, brands, tags } = this.state;
    return this.data.machines.filter((m) => {
      if (series && `${m.brand}::${m.series}` !== series) return false;
      if (cat !== 'all' && !m.cats.includes(cat)) return false;
      if (brands.size && !brands.has(m.brand)) return false;
      for (const tag of tags) if (!m.tags.includes(tag)) return false;
      if (q && !`${m.name} ${m.codes.join(' ')} ${m.family} ${m.tagline} ${this.brandName(m.brand)}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }

  badges(m) {
    return m.tags
      .map((id) => this.data.tags.find((t) => t.id === id))
      .filter(Boolean)
      .map((t) => `<li class="se-badge">${esc(t.name)}</li>`)
      .join('');
  }

  compareButton(m, className, label) {
    const picked = this.state.compare.includes(m.id);
    const full = this.state.compare.length >= MAX_COMPARE;
    return `<button type="button" class="${className}${picked && className.includes('se-btn--pick') ? ' is-on' : ''}" data-se-toggle="${esc(m.id)}"${!picked && full ? ' disabled' : ''} aria-pressed="${picked}">${esc(picked ? this.s.compare_on : label)}</button>`;
  }

  // Duża karta (alternatywy propozycji): zdjęcie i nazwa otwierają kartę maszyny.
  card(m) {
    const s = this.s;
    const picked = this.state.compare.includes(m.id);
    const keySpecs = [['perfText', m.specs.perfText], ['tankSolution', m.specs.tankSolution], ['waterlift', m.specs.waterlift]]
      .filter(([, value]) => value != null && value !== '')
      .map(([key, value]) => {
        const spec = this.specByKey.get(key);
        return `<div class="se-card__spec"><dt>${esc(spec.label)}</dt><dd>${esc(fmt(spec, value))}</dd></div>`;
      })
      .join('');
    const shop = m.shop
      ? `<a class="se-card__shop" href="${esc(m.shop.url)}">${esc(s.shop)}${m.shop.price ? ` · ${esc(m.shop.price)}` : ''}</a>`
      : '';
    return `
      <article class="se-card${picked ? ' is-picked' : ''}">
        <button type="button" class="se-card__media" data-se-detail="${esc(m.id)}" aria-label="${esc(fill(s.card_label, { name: m.name }))}" tabindex="-1">
          <img ${thumb(m.img, THUMB)} alt="${esc(`${this.brandName(m.brand)} ${m.name}`)}" loading="lazy" width="240" height="240">
        </button>
        <div class="se-card__head">
          <p class="se-card__brand">${esc(this.brandName(m.brand))}</p>
          <h3 class="se-card__name"><button type="button" class="se-card__title" data-se-detail="${esc(m.id)}">${esc(m.name)}</button></h3>
          <p class="se-card__family">${esc(m.family)}</p>
        </div>
        <p class="se-card__tagline">${esc(m.tagline)}</p>
        <ul class="se-card__badges">${this.badges(m)}</ul>
        <dl class="se-card__specs">${keySpecs}</dl>
        <div class="se-card__actions">
          <button type="button" class="se-btn" data-se-detail="${esc(m.id)}">${esc(s.details)}</button>
          ${this.compareButton(m, 'se-btn se-btn--pick', s.compare)}
          ${shop}
        </div>
      </article>`;
  }

  /* ------------------------------------------------------- karta maszyny */

  openDetail(id) {
    const machine = this.machine(id);
    if (!machine) return;
    this.openMachine = id;
    this.writeUrl();
    this.renderDetail(machine);
    if (!this.detailDialog.open) this.detailDialog.showModal();
    this.detailDialog.scrollTop = 0;
    this.loadAcc().then((acc) => {
      this.accData = acc;
      if (this.openMachine === id) this.renderAccessories(id, acc);
    });
  }

  renderDetail(m) {
    const s = this.s;
    this.detailDialog.querySelector('[data-se-detail-title]').textContent = m.name;
    const specRows = this.data.groups
      .map((group) => {
        const rows = this.data.specs
          .filter((spec) => spec.group === group.id && !isEmpty(m.specs[spec.key]))
          .map((spec) => `<tr><th scope="row">${esc(spec.label)}</th><td>${esc(fmt(spec, m.specs[spec.key]))}</td></tr>`)
          .join('');
        return rows ? `<tr class="se-detail__group"><th scope="row" colspan="2">${esc(group.name)}</th></tr>${rows}` : '';
      })
      .join('');
    const noise = m.specs.noiseText ? `<p class="se-detail__footnote">${esc(fill(s.noise_note, { text: m.specs.noiseText }))}</p>` : '';
    const shop = m.shop
      ? `<a class="se-btn" href="${esc(m.shop.url)}">${esc(s.shop)}${m.shop.price ? ` · ${esc(m.shop.price)}` : ''}</a>`
      : '';

    this.detailDialog.querySelector('[data-se-detail-body]').innerHTML = `
      <div class="se-detail">
        <div class="se-detail__top">
          <div class="se-detail__media"><img src="${esc(this.imgUrl(m))}" alt="${esc(`${this.brandName(m.brand)} ${m.name}`)}" width="320" height="320"></div>
          <div class="se-detail__intro">
            <p class="se-detail__family">${esc(this.brandName(m.brand))} · ${esc(m.family)}</p>
            <p class="se-detail__tagline">${esc(m.tagline)}</p>
            <p class="se-detail__desc">${esc(m.desc)}</p>
            <ul class="se-detail__hi">${m.hi.map((h) => `<li>${esc(h)}</li>`).join('')}</ul>
            <ul class="se-card__badges">${this.badges(m)}</ul>
            <div class="se-detail__actions">
              ${shop}
              <button type="button" class="se-btn${m.shop ? ' se-btn--ghost' : ''}" data-se-kit-open="${esc(m.id)}">${esc(s.kit_configure)}</button>
              ${this.compareButton(m, 'se-btn se-btn--ghost', s.compare_add)}
            </div>
          </div>
        </div>
        <section class="se-detail__section">
          <h3 class="se-detail__title">${esc(s.specs)}</h3>
          <table class="se-detail__specs">${specRows}</table>
          ${noise}
          <p class="se-detail__footnote">${esc(s.specs_note)}</p>
        </section>
        <section class="se-detail__section" data-se-acc>
          <h3 class="se-detail__title">${esc(s.acc_title)}</h3>
          <p class="se-detail__footnote">${esc(s.acc_loading)}</p>
        </section>
      </div>`;
  }

  renderAccessories(id, acc) {
    const s = this.s;
    const box = this.detailDialog.querySelector('[data-se-acc]');
    if (!box) return;
    const groups = acc && acc.machines ? acc.machines[id] : null;
    const title = `<h3 class="se-detail__title">${esc(s.acc_title)}</h3>`;
    if (!groups || !groups.length) {
      box.innerHTML = `${title}<p class="se-detail__footnote">${esc(acc ? s.acc_none : s.acc_error)}</p>`;
      return;
    }
    const html = groups
      .map((group) => {
        const items = group.c
          .filter((code) => shownItem(acc, code))
          .map((code) => {
            const item = acc.items[code];
            return `
              <li>
                <span class="se-acc__name">${esc(item.n)}</span>
                <span class="se-acc__code">${esc(item.k || code)}</span>
                ${item.d ? `<span class="se-acc__desc">${esc(item.d)}</span>` : ''}
              </li>`;
          })
          .join('');
        return items ? `<div class="se-acc__group"><h4 class="se-acc__title">${esc(group.t)}</h4><ul class="se-acc__list">${items}</ul></div>` : '';
      })
      .join('');
    box.innerHTML = `${title}<div class="se-acc">${html}</div><p class="se-detail__footnote">${esc(s.acc_note)}</p>`;
  }

  /* ---------------------------------------------------------- porównanie */

  toggleCompare(id) {
    const at = this.state.compare.indexOf(id);
    if (at >= 0) this.state.compare.splice(at, 1);
    else if (this.state.compare.length < MAX_COMPARE) this.state.compare.push(id);
    else return;
    const focus = focusMark([this.compareDialog, this.detailDialog]);
    this.renderStage();
    if (this.compareDialog.open) this.renderCompare();
    if (this.detailDialog.open && this.openMachine) {
      const machine = this.machine(this.openMachine);
      this.renderDetail(machine);
      if (this.accData !== undefined) this.renderAccessories(machine.id, this.accData);
    }
    restoreFocus(focus);
  }

  openCompare() {
    this.renderCompare();
    if (!this.compareDialog.open) this.compareDialog.showModal();
  }

  renderCompare() {
    const s = this.s;
    const picked = this.state.compare.map((id) => this.machine(id)).filter(Boolean);
    const body = this.compareDialog.querySelector('[data-se-compare-body]');
    if (picked.length < 2) {
      body.innerHTML = `<p class="se-dialog__hint">${esc(s.compare_min)}</p>`;
      return;
    }

    const head = picked
      .map((m) => `
        <th scope="col">
          <img ${thumb(m.img, 84)} alt="" width="96" height="96" loading="lazy">
          <span class="se-compare__brand">${esc(this.brandName(m.brand))}</span>
          <span class="se-compare__name">${esc(m.name)}</span>
          <span class="se-compare__family">${esc(m.family)}</span>
          ${m.shop && m.shop.price
            ? `<span class="se-compare__price">${esc(m.shop.price)}</span>`
            : `<span class="se-compare__price se-compare__price--ask">${esc(s.price_ask_short)}</span>`}
          <button type="button" class="se-compare__shop" data-se-detail="${esc(m.id)}">${esc(s.details)}</button>
          ${m.shop ? `<a class="se-compare__shop" href="${esc(m.shop.url)}">${esc(s.shop)}</a>` : ''}
          <button type="button" class="se-compare__remove" data-se-toggle="${esc(m.id)}">${esc(s.remove)}</button>
        </th>`)
      .join('');

    const rows = this.data.groups
      .map((group) => {
        const groupRows = this.data.specs
          .filter((spec) => spec.group === group.id)
          .map((spec) => {
            const values = picked.map((m) => m.specs[spec.key]);
            if (values.every(isEmpty)) return '';
            // Najlepsza wartość podświetlona tylko, gdy jest jedna.
            let best = null;
            if (spec.type === 'num' && spec.better) {
              const nums = values.filter((v) => typeof v === 'number');
              if (nums.length > 1) {
                const candidate = spec.better === 'up' ? Math.max(...nums) : Math.min(...nums);
                if (nums.filter((v) => v === candidate).length === 1) best = candidate;
              }
            }
            const cells = values.map((v) => `<td${best != null && v === best ? ' class="is-best"' : ''}>${esc(fmt(spec, v))}</td>`).join('');
            return `<tr><th scope="row">${esc(spec.label)}</th>${cells}</tr>`;
          })
          .join('');
        return groupRows
          ? `<tr class="se-compare__group"><th scope="row" colspan="${picked.length + 1}">${esc(group.name)}</th></tr>${groupRows}`
          : '';
      })
      .join('');

    const notes = picked
      .map((m) => `
        <div class="se-compare__note">
          <h4>${esc(m.name)}</h4>
          <p>${esc(m.desc)}</p>
          <ul>${m.hi.map((h) => `<li>${esc(h)}</li>`).join('')}</ul>
          <button type="button" class="se-link" data-se-detail="${esc(m.id)}">${esc(s.details_full)}</button>
        </div>`)
      .join('');

    body.innerHTML = `
      <div class="se-compare__scroll">
        <table class="se-compare"><thead><tr><td></td>${head}</tr></thead><tbody>${rows}</tbody></table>
      </div>
      <div class="se-compare__notes">${notes}</div>`;
  }

  // Dok jest widoczny zawsze — podpowiada, że porównanie istnieje, zanim cokolwiek zaznaczono.
  renderTray() {
    const s = this.s;
    const picked = this.state.compare.map((id) => this.machine(id)).filter(Boolean);
    const compare = this.dock.querySelector('[data-se-compare-open]');
    this.dock.querySelector('[data-se-clear]').hidden = picked.length === 0;
    compare.disabled = picked.length < 2;
    compare.classList.toggle('se-btn--ghost', picked.length < 2);
    compare.textContent = picked.length ? fill(s.compare_count, { n: picked.length }) : s.compare;

    if (!picked.length) {
      this.traySlots.innerHTML = `<p class="se-tray__hint">${esc(fill(s.dock_hint, { n: MAX_COMPARE }))}</p>`;
      return;
    }
    const more = MAX_COMPARE - picked.length;
    this.traySlots.innerHTML =
      picked
        .map((m) => `
          <div class="se-tray__slot">
            <img ${thumb(m.img, 28)} alt="" width="40" height="40" loading="lazy">
            <span>${esc(m.name)}</span>
            <button type="button" class="se-tray__remove" data-se-toggle="${esc(m.id)}" aria-label="${esc(fill(s.dock_remove, { name: m.name }))}">✕</button>
          </div>`)
        .join('') +
      (more > 0 ? `<p class="se-tray__hint">${esc(fill(s.dock_more, { n: more, machines: forms(more, s.forms_machines_acc) }))}</p>` : '');
  }

  /* ------------------------------------------------- konfigurator zestawu */

  onKitClick(event) {
    const target = event.target;
    const on = (selector) => target.closest(selector);
    let node;

    if (target === this.kitDialog || on('[data-se-close]')) {
      this.kitDialog.close();
    } else if ((node = on('[data-se-kit-machine]'))) {
      this.setKitMachine(node.dataset.seKitMachine);
      this.renderKit();
      this.kitDialog.scrollTop = 0;
    } else if (on('[data-se-kit-change]')) {
      this.kit.machineId = null;
      this.renderKit();
      this.kitDialog.scrollTop = 0;
    } else if (on('[data-se-kit-restore]')) {
      this.restoreKit();
    } else if ((node = on('[data-se-kit-copy-code]'))) {
      this.copyText(this.kitCode(), node);
    } else if (on('[data-se-kit-summary]')) {
      this.kitStage = 'summary';
      this.cartNote = null;
      this.renderKit();
      this.kitDialog.scrollTop = 0;
    } else if (on('[data-se-kit-back]')) {
      this.kitStage = 'pick';
      this.renderKit();
    } else if ((node = on('[data-se-kit-cart]'))) {
      this.addKitToCart(node);
    } else if ((node = on('[data-se-kit-copy]'))) {
      this.copyText(this.kitAsText(), node);
    } else if (on('[data-se-kit-send]')) {
      this.showQuoteForm();
    }
  }

  // Treść zapytania wpisujemy od razu (klient widzi, co wysyła), ale nie nadpisujemy tekstu zmienionego ręcznie.
  showQuoteForm() {
    const wrap = this.kitDialog.querySelector('[data-se-kit-formwrap]');
    wrap.hidden = false;
    const field = wrap.querySelector('[data-se-kit-message]');
    if (field && (!field.value.trim() || field.dataset.seAuto === field.value)) {
      field.value = this.kitAsText();
      field.dataset.seAuto = field.value;
    }
    // Okno przewija się samo (strona stoi), a jego głowa z polem kodu jest sticky — formularz 16 px pod nią.
    const dialog = this.kitDialog;
    const head = dialog.querySelector('.se-dialog__top');
    const top = wrap.getBoundingClientRect().top - dialog.getBoundingClientRect().top + dialog.scrollTop;
    dialog.scrollTo({ top: top - (head?.offsetHeight || 0) - 16, behavior: reducedMotion() ? 'auto' : 'smooth' });
    wrap.querySelector('input')?.focus({ preventScroll: true });
  }

  /*
   * Kod zestawu z pola doradcy albo okna zestawu — ta sama odpowiedź w obu miejscach. Bez listy osprzętu maska
   * nie ma do czego się odnieść, więc najpierw ją wczytujemy.
   */
  useKitCode(raw, say, done) {
    const s = this.s;
    if (!raw) return say(s.code_empty, 'warn');
    if (!this.accData) {
      say(s.code_wait, 'warn');
      this.loadAcc().then((acc) => {
        this.accData = acc;
        if (acc) this.useKitCode(raw, say, done);
        else say(s.acc_error, 'error');
      });
      return;
    }
    const parsed = decodeKitCode(raw, this.data.machines, this.accData);
    if (!parsed) return say(s.code_bad, 'error');
    this.kit.machineId = parsed.machineId;
    this.kit.picked = parsed.picked;
    // Zestaw z kodu jest kompletny — domyślne „Zalecane” nie mogą do niego niczego dopisać.
    this.kitDefaults = true;
    done(parsed);
  }

  restoreKit() {
    const input = this.kitDialog.querySelector('[data-se-kit-code-input]');
    const note = this.kitDialog.querySelector('[data-se-kit-code-note]');
    const say = (text, state) => {
      note.textContent = text;
      note.dataset.seState = state;
    };
    this.useKitCode(input.value.trim(), say, (parsed) => {
      this.renderKit();
      say(parsed.stale ? this.s.code_stale : this.s.code_ok, parsed.stale ? 'warn' : 'ok');
    });
  }

  openKitFromCode() {
    const input = this.stageNode.querySelector('[data-se-head-code]');
    const note = this.stageNode.querySelector('[data-se-head-code-note]');
    if (!input || !note) return;
    const say = (text, state) => {
      note.textContent = text;
      note.dataset.seState = state;
    };
    this.useKitCode(input.value.trim(), say, (parsed) => {
      say('', '');
      this.openKit();
      if (parsed.stale) {
        const kitNote = this.kitDialog.querySelector('[data-se-kit-code-note]');
        kitNote.textContent = this.s.code_stale;
        kitNote.dataset.seState = 'warn';
      }
    });
  }

  copyText(text, button) {
    if (!text || !navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => {
      button.classList.add('is-copied');
      clearTimeout(button.seCopied);
      button.seCopied = setTimeout(() => button.classList.remove('is-copied'), 1600);
    }, () => {});
  }

  // Zawsze ekran wyboru; bez maszyny — siatka maszyn z bieżącego filtra przeglądu.
  openKit(machineId) {
    this.kitStage = 'pick';
    this.cartNote = null;
    if (machineId) this.setKitMachine(machineId);
    // null = poprzednia próba wczytania osprzętu nieudana; ponawiamy, więc znów „Wczytuję…”.
    if (this.accData === null) this.accData = undefined;
    this.renderKit();
    if (!this.kitDialog.open) this.kitDialog.showModal();
    this.kitDialog.scrollTop = 0;
    this.loadAcc().then((acc) => {
      this.accData = acc;
      if (this.kitDialog.open) this.renderKit();
    });
  }

  // Zalecane zaznaczamy raz na maszynę — zmiana maszyny resetuje wybór.
  setKitMachine(id) {
    if (this.kit.machineId === id) return;
    this.kit.machineId = id;
    this.kit.picked = new Set();
    this.kitDefaults = false;
  }

  kitGroups() {
    if (!this.accData || !this.kit.machineId) return null;
    return this.accData.machines[this.kit.machineId] || [];
  }

  kitCode() {
    const machine = this.machine(this.kit.machineId);
    return machine ? encodeKitCode(machine.no, kitOptions(this.accData, machine.id), this.kit.picked) : '';
  }

  machineBar(machine, change) {
    return `
      <div class="se-kit__machine-bar">
        <img ${thumb(machine.img, 56)} alt="" width="64" height="64">
        <div>
          <p class="se-kit__machine-name">${esc(machine.name)}</p>
          <p class="se-kit__machine-family">${esc(this.brandName(machine.brand))} · ${esc(machine.family)}</p>
        </div>
        ${change ? `<button type="button" class="se-link" data-se-kit-change>${esc(this.s.kit_change)}</button>` : ''}
      </div>`;
  }

  renderKit() {
    const s = this.s;
    const body = this.kitDialog.querySelector('[data-se-kit-body]');
    const machine = this.machine(this.kit.machineId);

    if (!machine) {
      const visible = this.visible();
      const list = visible.length ? visible : this.data.machines;
      body.innerHTML = `
        <p class="se-kit__lead">${esc(s.kit_lead)}</p>
        <div class="se-kit__machines">${list
          .map((m) => `
            <button type="button" class="se-kit__machine" data-se-kit-machine="${esc(m.id)}">
              <img ${thumb(m.img, 64)} alt="" width="72" height="72" loading="lazy">
              <span class="se-kit__machine-name">${esc(m.name)}</span>
              <span class="se-kit__machine-family">${esc(this.brandName(m.brand))}</span>
            </button>`)
          .join('')}</div>`;
      this.kitDialog.querySelector('[data-se-kit-formwrap]').hidden = true;
      this.renderKitBar();
      return;
    }

    const groups = this.kitGroups();
    if (groups && !this.kitDefaults) {
      this.kitDefaults = true;
      for (const group of groups) {
        if (!/zalecane/i.test(group.t)) continue;
        for (const code of group.c) if (shownItem(this.accData, code)) this.kit.picked.add(code);
      }
    }

    if (this.kitStage === 'summary') {
      body.innerHTML = this.machineBar(machine, false) + this.renderKitSummary();
    } else {
      const pending = esc(this.accData === null ? s.acc_error : s.kit_loading);
      const sections = groups === null ? `<p class="se-detail__footnote">${pending}</p>` : this.kitSections(groups);
      body.innerHTML = this.machineBar(machine, true) + sections;
    }
    this.renderKitBar();
  }

  // Wybór bez cen; „W zestawie” = pozycje stałe (✓), reszta to pola wyboru.
  kitSections(groups) {
    const s = this.s;
    if (!groups.length) return `<p class="se-detail__footnote">${esc(s.kit_none)}</p>`;
    return groups
      .map((group) => {
        const included = /w zestawie/i.test(group.t);
        const rows = group.c
          .filter((code) => shownItem(this.accData, code))
          .map((code) => {
            const item = this.accData.items[code];
            const desc = item.d ? `<span class="se-kit__desc">${esc(item.d)}</span>` : '';
            const label = `<span class="se-kit__name">${esc(item.n)}<span class="se-kit__code">${esc(item.k || code)}</span></span>`;
            if (included) {
              return `<li class="se-kit__row is-fixed"><span class="se-kit__check" aria-hidden="true">✓</span><span class="se-kit__text">${label}${desc}</span></li>`;
            }
            const id = `se-kit-${slug(this.kit.machineId)}-${slug(code)}`;
            return `
              <li class="se-kit__row">
                <input type="checkbox" id="${id}" data-se-kit-item="${esc(code)}"${this.kit.picked.has(code) ? ' checked' : ''}>
                <label for="${id}" class="se-kit__text">${label}${desc}</label>
              </li>`;
          })
          .join('');
        if (!rows) return '';
        return `
          <section class="se-kit__group">
            <h3 class="se-kit__group-title">${esc(group.t)}${included ? ` <span class="se-kit__tag">${esc(s.kit_included)}</span>` : ''}</h3>
            <ul class="se-kit__list">${rows}</ul>
          </section>`;
      })
      .join('');
  }

  /** Pozycje zestawu w kolejności grup: „W zestawie” i zaznaczone (bez ukrytych). */
  kitLines() {
    const lines = [];
    for (const group of this.kitGroups() || []) {
      const included = /w zestawie/i.test(group.t);
      const codes = group.c.filter((code) => shownItem(this.accData, code) && (included || this.kit.picked.has(code)));
      if (codes.length) lines.push({ title: group.t, items: codes.map((code) => ({ code, ...this.accData.items[code] })) });
    }
    return lines;
  }

  /*
   * Zestaw rozpisany wg dostępności: ready (produkt na stanie), soldOut (produkt bez stanu), onRequest (brak produktu).
   * Koszyk tylko dla całego zestawu w `ready` — zakaz częściowego koszyka.
   */
  kitCart() {
    const machine = this.machine(this.kit.machineId);
    const lines = machine ? [{ name: machine.name, note: this.brandName(machine.brand), shop: machine.shop || null }] : [];
    for (const group of this.kitLines()) {
      for (const item of group.items) lines.push({ name: item.n, note: item.k || item.code, shop: this.shopFor(item) });
    }
    const ready = lines.filter((l) => l.shop && l.shop.variant && l.shop.available);
    const soldOut = lines.filter((l) => l.shop && l.shop.variant && !l.shop.available);
    const onRequest = lines.filter((l) => !(l.shop && l.shop.variant));
    return { lines, ready, soldOut, onRequest, canBuy: ready.length > 0 && !soldOut.length && !onRequest.length };
  }

  kitAsText() {
    const s = this.s;
    const machine = this.machine(this.kit.machineId);
    if (!machine) return '';
    const code = this.kitCode();
    const cartState = this.kitCart();
    const statusOf = (name) => {
      if (cartState.ready.some((l) => l.name === name)) return s.status_ready;
      if (cartState.soldOut.some((l) => l.name === name)) return s.status_sold_out;
      return s.status_on_request;
    };
    const machinePrice = machine.shop && machine.shop.price ? `, ${machine.shop.price}` : '';
    const parts = [
      fill(s.msg_title, { code }),
      '',
      fill(s.msg_machine, { name: machine.name, brand: this.brandName(machine.brand), family: machine.family, price: machinePrice }),
      '',
    ];
    for (const group of this.kitLines()) {
      parts.push(group.title.toUpperCase());
      for (const item of group.items) {
        const product = this.shopFor(item);
        parts.push(`- ${item.n} [${item.k || item.code}] — ${statusOf(item.n)}${product && product.price ? `, ${product.price}` : ''}`);
      }
      parts.push('');
    }
    parts.push(fill(s.msg_code, { code }), s.msg_restore, '', s.msg_notes, '');
    return parts.join('\n');
  }

  async addKitToCart(button) {
    const s = this.s;
    const { ready, canBuy } = this.kitCart();
    if (!canBuy) return;
    const code = this.kitCode();
    button.disabled = true;
    this.cartNote = { text: s.cart_adding, state: 'warn' };
    this.paintCartNote();
    try {
      const items = ready.map((line) => ({ id: line.shop.variant, quantity: 1, properties: { [KIT_PROPERTY]: code || '—' } }));
      await cart.add(items, { source: 'advisor', sourceEl: button });
      this.cartNote = { text: fill(s.cart_added, { n: ready.length, items: forms(ready.length, s.forms_items_acc), code }), state: 'ok' };
    } catch (error) {
      this.cartNote = { text: fill(s.cart_failed, { reason: error.message || s.cart_rejected }), state: 'error' };
    }
    button.disabled = false;
    this.renderKitBar();
  }

  // Notka to stały region live (w Liquidzie): zmieniamy tylko jej tekst, żeby czytniki ją odczytały.
  paintCartNote() {
    const note = this.kitDialog.querySelector('[data-se-cart-note]');
    if (!note) return;
    const value = this.kitStage === 'summary' ? this.cartNote : null;
    note.textContent = value ? value.text : '';
    note.dataset.seState = value ? value.state : '';
  }

  renderKitBar() {
    const s = this.s;
    const bar = this.kitDialog.querySelector('[data-se-kit-bar]');
    const main = bar.querySelector('[data-se-kit-bar-main]');
    this.paintCartNote();
    if (!this.kit.machineId || !this.accData) {
      main.innerHTML = '';
      bar.hidden = true;
      return;
    }
    bar.hidden = false;

    if (this.kitStage !== 'summary') {
      const count = this.kitLines().reduce((sum, group) => sum + group.items.length, 0);
      const code = this.kitCode();
      main.innerHTML = `
        <div class="se-kit__summary">
          <p class="se-kit__count">${esc(s.kit_count)} <strong>${count}</strong> ${esc(forms(count, s.forms_items))}</p>
          ${code
            ? `<button type="button" class="se-kit__code-chip" data-se-kit-copy-code title="${esc(s.kit_copy_title)}">
                 <span class="se-kit__code-label" data-copied="${esc(s.kit_copied)}">${esc(s.kit_code)}</span>
                 <span class="se-kit__code-value">${esc(code)}</span>
               </button>`
            : ''}
        </div>
        <div class="se-kit__actions">
          <button type="button" class="se-btn se-btn--ghost" data-se-kit-copy>${esc(s.kit_copy_list)}</button>
          <button type="button" class="se-btn" data-se-kit-summary>${esc(fill(s.kit_summary, { n: count }))}</button>
        </div>`;
      return;
    }

    const { canBuy, ready } = this.kitCart();
    const total = ready.reduce((sum, line) => sum + (line.shop.cents || 0), 0);
    main.innerHTML = `
      <div class="se-kit__summary"><button type="button" class="se-link" data-se-kit-back>${esc(s.kit_back)}</button></div>
      <div class="se-kit__actions">
        <button type="button" class="se-btn${canBuy ? ' se-btn--ghost' : ''}" data-se-kit-send>${esc(canBuy ? s.kit_send : s.kit_ask)}</button>
        <button type="button" class="se-btn" data-se-kit-cart${canBuy ? '' : ' disabled'}>${esc(s.kit_cart)}${canBuy && total ? ` · ${esc(money(total))}` : ''}</button>
      </div>`;
  }

  renderKitSummary() {
    const s = this.s;
    const { lines, ready, soldOut, onRequest, canBuy } = this.kitCart();
    const total = ready.reduce((sum, line) => sum + (line.shop.cents || 0), 0);
    const tags = {
      ready: `<span class="se-sum__tag se-sum__tag--ok">${esc(s.status_ready)}</span>`,
      soldOut: `<span class="se-sum__tag se-sum__tag--warn">${esc(s.status_sold_out)}</span>`,
      onRequest: `<span class="se-sum__tag">${esc(s.status_on_request)}</span>`,
    };
    const row = (line, state) => `
      <li class="se-sum__row">
        <span class="se-sum__name">${esc(line.name)}${line.note ? `<span class="se-sum__note">${esc(line.note)}</span>` : ''}</span>
        ${tags[state]}
        <span class="se-sum__price">${line.shop && line.shop.price ? esc(line.shop.price) : `<span class="se-sum__ask">${esc(s.quote)}</span>`}</span>
      </li>`;
    const rows = [['ready', ready], ['soldOut', soldOut], ['onRequest', onRequest]]
      .map(([state, list]) => list.map((line) => row(line, state)).join(''))
      .join('');
    const parts = [
      soldOut.length ? fill(s.sum_sold_out, { n: soldOut.length, items: forms(soldOut.length, s.forms_items_are) }) : '',
      onRequest.length ? fill(s.sum_on_request, { n: onRequest.length, items: forms(onRequest.length, s.forms_items_acc) }) : '',
    ].filter(Boolean);
    const hint = canBuy
      ? `<p class="se-sum__hint">${esc(s.sum_ok)}</p>`
      : `<p class="se-sum__hint se-sum__hint--warn">${esc(`${s.sum_warn} ${parts.join(', ')}. ${s.sum_warn_end}`)}</p>`;

    return `
      <div class="se-sum">
        <h3 class="se-sum__title">${esc(s.sum_title)}</h3>
        <p class="se-sum__lead">${lines.length} ${esc(forms(lines.length, s.forms_items))} · ${esc(s.sum_code)} <strong>${esc(this.kitCode())}</strong></p>
        <ul class="se-sum__list">${rows}</ul>
        <div class="se-sum__total">
          <span>${esc(fill(s.sum_ready, { ready: ready.length, all: lines.length }))}</span>
          <strong>${total ? esc(money(total)) : '—'}</strong>
        </div>
        ${hint}
      </div>`;
  }
}

if (!customElements.get('machine-advisor')) customElements.define('machine-advisor', MachineAdvisor);
